
import { global_marketname as marketname_de } from '../api/market_de';
import { global_marketname as marketname_en } from '../api/market_en';
import { locales, Locale } from '../i18n';
import { global_marketname as marketname_tr } from '../api/market_tr';
import { global_marketname as marketname_fr } from '../api/market_fr';
import { global_marketname as marketname_nl } from '../api/market_nl';
import { global_marketlist as marketlist_de } from '../api/market_de';
import { global_marketlist as marketlist_en } from '../api/market_en';
import { global_marketlist as marketlist_tr } from '../api/market_tr';
import { global_marketlist as marketlist_fr } from '../api/market_fr';
import { global_marketlist as marketlist_nl } from '../api/market_nl';

export const getMarketList = (lang: string) => {
  if (lang === 'tr') return marketlist_tr;
  if (lang === 'en') return marketlist_en;
  if (lang === 'fr') return marketlist_fr;
  if (lang === 'nl') return marketlist_nl;
  return marketlist_de;
};

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const formatOddsButtonLabel = (label: string, homeTeam: string, awayTeam: string, locale: string): string => {
  if (!label) return label;
  
  let formatted = label;
  
  if (homeTeam && formatted.includes(homeTeam)) {
    formatted = formatted.replace(new RegExp(`\\b${escapeRegExp(homeTeam)}\\b`, 'gi'), '1');
    if (formatted === label) formatted = formatted.replace(homeTeam, '1');
  }
  if (awayTeam && formatted.includes(awayTeam)) {
    formatted = formatted.replace(new RegExp(`\\b${escapeRegExp(awayTeam)}\\b`, 'gi'), '2');
    if (formatted === label) formatted = formatted.replace(awayTeam, '2');
  }

  const lower = formatted.toLowerCase().trim();

  // Single word checks
  if (lower === 'über' || lower === 'over' || lower === 'üst') return '+';
  if (lower === 'unter' || lower === 'under' || lower === 'alt') return '-';
  
  if (lower === 'ja' || lower === 'yes' || lower === 'evet') {
    return locale === 'tr' ? 'E' : locale === 'en' ? 'Y' : 'J';
  }
  if (lower === 'nein' || lower === 'no' || lower === 'hayır') {
    return locale === 'tr' ? 'H' : 'N';
  }

  // Replace connectors
  formatted = formatted.replace(/\bund\b/gi, '&');
  formatted = formatted.replace(/\band\b/gi, '&');
  formatted = formatted.replace(/\bve\b/gi, '&');

  formatted = formatted.replace(/\boder\b/gi, '/');
  formatted = formatted.replace(/\bor\b/gi, '/');
  formatted = formatted.replace(/\bveya\b/gi, '/');

  // Replace localized Yes/No in combinations
  formatted = formatted.replace(/\bJa\b/gi, 'J');
  formatted = formatted.replace(/\bYes\b/gi, 'Y');
  formatted = formatted.replace(/\bEvet\b/gi, 'E');
  
  formatted = formatted.replace(/\bNein\b/gi, 'N');
  formatted = formatted.replace(/\bNo\b/gi, 'N');
  formatted = formatted.replace(/\bHayır\b/gi, 'H');

  formatted = formatted.replace(/\bunentschieden\b/gi, 'X');
  formatted = formatted.replace(/\bdraw\b/gi, 'X');
  formatted = formatted.replace(/\bberaberlik\b/gi, 'X');

  formatted = formatted.replace(/\bÜber\b/gi, '+');
  formatted = formatted.replace(/\bOver\b/gi, '+');
  formatted = formatted.replace(/\bÜst\b/gi, '+');

  formatted = formatted.replace(/\bUnter\b/gi, '-');
  formatted = formatted.replace(/\bUnder\b/gi, '-');
  formatted = formatted.replace(/\bAlt\b/gi, '-');

  return formatted;
};

export const resolveMarketName = (marketId: string, locale: string, specMap: Record<string, string> = {}, defaultName?: string, eventName?: string): string | null => {
  let marketName: string | null = null;

  const list = getMarketList(locale);
  const market = (list as any)[marketId];
  if (market && market.name) {
    marketName = market.name;
  }

  if (!marketName && defaultName) {
    marketName = defaultName;
  }
  
  if (typeof marketName === 'string') {
    if (marketName.includes('{$competitor1}') || marketName.includes('{$competitor2}')) {
      const teams = eventName?.split(' - ') || [];
      marketName = marketName.replace(/\{\$competitor1\}/g, teams[0] || 'Heim');
      marketName = marketName.replace(/\{\$competitor2\}/g, teams[1] || 'Gast');
    }

    // Handle player specifiers: extract player name from defaultName or use explicit playerName in specMap
    if (specMap.player && specMap.player.startsWith('sr:player:')) {
      let playerName: string | undefined = specMap.playerName;
      if (!playerName && defaultName) {
        const playerMatch = defaultName.match(/\(([^)]+)\)\s*$/);
        playerName = playerMatch ? playerMatch[1] : undefined;
      }
      playerName = playerName || specMap.player;
      marketName = marketName.replace(/\{%player\}/g, playerName);
      marketName = marketName.replace(/\{player\}/g, playerName);
      // If the market name doesn't have a placeholder but has the player in defaultName, append it
      if (!marketName.includes(playerName) && playerName !== specMap.player) {
        marketName = `${marketName} (${playerName})`;
      }
    }

    Object.entries(specMap).forEach(([k, v]) => {
      // Translate known custom specifier values before replacing
      let val = v;
      if (val === 'sr:correct_score:below:5-5') {
        val = locale === 'tr' ? 'Diğer' : locale === 'de' ? 'Andere' : 'Other';
      } else if (k === 'player' && val.startsWith('sr:')) {
        return; // Skip player sr: values, already handled above
      } else if (val.startsWith('sr:')) {
        val = ''; // hide unknown sr: values
      }
      marketName = marketName!.replace(new RegExp('\\{[!+-]?' + k + '\\}', 'g'), val);
    });
    
    // Clean up any remaining raw API identifiers if they came from defaultName
    marketName = marketName.replace(/sr:[a-z0-9_:-]+/gi, '').trim();

    if (locale === 'tr') {
        marketName = marketName.replace(/Correct score/gi, 'Doğru skor');
    } else if (locale === 'de') {
        marketName = marketName.replace(/Correct score/gi, 'Genaues Ergebnis');
    }

    // Remove any remaining unresolved placeholders like {!goalnr}
    marketName = marketName.replace(/\{[!+-]?[^}]+\}/g, '').trim();
    
    // Replace ordinals (1st, 2nd, 3rd, 4th, etc.) with 1., 2., 3., 4.
    marketName = marketName.replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');

    return marketName;
  }

  return null;
};

export const resolveOutcomeName = (marketId: string, outcomeId: string, locale: string, specMap: Record<string, string> = {}, eventName?: string, defaultName?: string): string => {
  const list = getMarketList(locale);
  const market = (list as any)[marketId];
  
  let outcomeName: string;
  if (market && market[outcomeId]) {
    outcomeName = market[outcomeId];
  } else {
    outcomeName = defaultName || outcomeId;
  }

  if (typeof outcomeName === 'string') {
    if (outcomeName.includes('{$competitor1}') || outcomeName.includes('{$competitor2}')) {
      const teams = eventName?.split(' - ') || [];
      outcomeName = outcomeName.replace(/\{\$competitor1\}/g, teams[0] || 'Heim');
      outcomeName = outcomeName.replace(/\{\$competitor2\}/g, teams[1] || 'Gast');
    }

    Object.entries(specMap).forEach(([k, v]) => {
      if (k === 'hcp') {
        outcomeName = outcomeName.replace(/\{\+hcp\}/g, v.startsWith('-') ? v : `+${v}`);
        outcomeName = outcomeName.replace(/\{\-hcp\}/g, v.startsWith('-') ? v.substring(1) : `-${v}`);
      }
      // Skip sr:player values, use defaultName (API name) which already contains the player name
      if (k === 'player' && v.startsWith('sr:')) {
        return;
      }
      outcomeName = outcomeName.replace(new RegExp('\\{[!+-]?' + k + '\\}', 'g'), v);
    });

    // Clean up any remaining sr: identifiers
    outcomeName = outcomeName.replace(/sr:[a-z0-9_:-]+/gi, '').trim();

    // Remove any remaining unresolved placeholders like {!goalnr}
    outcomeName = outcomeName.replace(/\{[!+-]?[^}]+\}/g, '');

    // Restore {player} if it was skipped and outcomeName is just the ID? No, if it's still {player} or something similar.
    if (outcomeName === '') {
       // If the regex completely removed the string (e.g. because it was ONLY the ID), revert to original
       outcomeName = market?.[outcomeId] || defaultName || outcomeId;
       // If it's the raw ID, keep it so it's not totally blank
    }

    // Replace ordinals (1st, 2nd, 3rd, 4th, etc.) with 1., 2., 3., 4.
    outcomeName = outcomeName.replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }

  return translateOutcomeLabel(outcomeName, locale);
};

export const parseSpecifiers = (specStr?: string): Record<string, string> => {
  const map: Record<string, string> = {};
  if (specStr) {
    specStr.split('|').forEach(s => {
      const [k, v] = s.split('=');
      if (k && v) map[k] = v;
    });
  }
  return map;
};




export const getGlobalMarketName = (lang: string) => {
  if (lang === 'tr') return marketname_tr;
  if (lang === 'en') return marketname_en;
  if (lang === 'fr') return marketname_fr;
  if (lang === 'nl') return marketname_nl;
  return marketname_de;
};

const marketIdLocales: Record<number | string, Record<string, string>> = {
  1: { tr: 'Maç Sonucu', en: 'Match Result', de: 'Tipp' }, // 1x2 Tipp
  7: { tr: 'Kalan Süre Sonucu', en: 'Rest of Match', de: 'Tipp Restzeit' },
  8: { tr: 'Sıradaki Gol', en: 'Next Goal', de: 'Nächstes Tor' },
  10: { tr: 'Çifte Şans', en: 'Double Chance', de: 'Doppelte Chance' },
  11: { tr: 'Beraberlikte İade', en: 'Draw No Bet', de: 'Draw No Bet' },
  14: { tr: 'Handikap', en: 'Handicap', de: 'Handicap' },
  18: { tr: 'Alt/Üst', en: 'Over/Under', de: 'Über/Unter' },
  19: { tr: 'Ev Sahibi Alt/Üst', en: 'Home O/U', de: 'Heim Über/Unter' },
  20: { tr: 'Deplasman Alt/Üst', en: 'Away O/U', de: 'Auswärts Über/Unter' },
  21: { tr: 'İlk Yarı Alt/Üst', en: '1st Half O/U', de: 'Halbzeit-Über/Unter' },
  28: { tr: 'Tek/Çift', en: 'Odd/Even', de: 'Gerade/Ungerade' },
  29: { tr: 'Karşılıklı Gol', en: 'Both Teams to Score', de: 'Beide Teams treffen' },
  50: { tr: 'H2H', en: 'Head-To-Head', de: 'Head-To-Head' },
  60: { tr: 'İlk Yarı Sonucu', en: '1st Half Result', de: '1. Halbzeit' },
  61: { tr: '1. Yarı Handikap', en: '1st Half Handicap', de: '1.Hz Handicap' },
  62: { tr: '1. Yarı Çifte Şans', en: '1st Half Double Chance', de: '1.Hz Doppelte Chance' },
  63: { tr: '1. Yarı Ev Sahibi A/Ü', en: '1st Half Home O/U', de: '1.Hz Heim Über/Unter' },
  64: { tr: '1. Yarı Deplasman A/Ü', en: '1st Half Away O/U', de: '1.Hz Auswärts Über/Unter' },
  65: { tr: '1. Yarı Tek/Çift', en: '1st Half Odd/Even', de: '1.Hz Gerade/Ungerade' },
  73: { tr: '1. Yarı Kalan Süre', en: '1st Half Rest Time', de: '1.Hz Tipp Restzeit' },
  121: { tr: '2. Yarı Handikap', en: '2nd Half Handicap', de: '2. Halbzeit Handicap' },
  122: { tr: '2. Yarı Alt/Üst', en: '2nd Half O/U', de: '2. Halbzeit Über/Unter' },
  148: { tr: 'Set Kazananı', en: 'Set Winner', de: 'Satz Sieger' },
  155: { tr: 'Set Handikapı', en: 'Set Handicap', de: 'Satz-Handicap' },
  220: { tr: 'Uzatmalar Olur mu?', en: 'Overtime Yes/No', de: 'Verlängerung Ja/Nein' },
  238: { tr: 'Toplam Sayı Alt/Üst', en: 'Total Points O/U', de: 'Gesamtpunkte Über/Unter' }, // For Volleyball / Table Tennis
  310: { tr: 'Set Toplam Sayı A/Ü', en: 'Set Total Points O/U', de: 'Satz Gesamtpunkte Über/Unter' },
  341: { tr: 'Çeyrek Sonucu', en: 'Quarter Result', de: 'Viertel 1x2' },
  342: { tr: 'Çeyrek Alt/Üst', en: 'Quarter O/U', de: 'Viertel Über/Unter' },
  
  // Custom strings to fallback
  'Markt Wählen': { tr: 'Market Seç', en: 'Select Market', de: 'Markt Wählen' },
  
  // Specific mappings for sports that don't use 1 or 18 natively for main markets
  251: { tr: 'Kazanan', en: 'Winner', de: 'Sieger' }, // Baseball
  186: { tr: 'Kazanan', en: 'Winner', de: 'Sieger' }, // Tennis / Darts
  219: { tr: 'Kazanan', en: 'Winner', de: 'Sieger' }, // Basketball
  187: { tr: 'Handikap', en: 'Handicap', de: 'Handicap' }, // Tennis
  203: { tr: 'Set Handikapı', en: 'Set Handicap', de: 'Satz-Handicap' }, // Tennis Set
  246: { tr: 'Set Handikapı', en: 'Set Handicap', de: 'Satz-Handicap' }, // TT Set
  237: { tr: 'Handikap', en: 'Handicap', de: 'Handicap' }, // TT Handicap
  189: { tr: 'Alt/Üst', en: 'Over/Under', de: 'Über/Unter' }, // Tennis Total Games
  204: { tr: 'Set Alt/Üst', en: 'Set Over/Under', de: 'Satz Über/Unter' }, // Tennis Set Games
  258: { tr: 'Alt/Üst', en: 'Over/Under', de: 'Über/Unter' }, // Baseball Total
  225: { tr: 'Alt/Üst', en: 'Over/Under', de: 'Über/Unter' }, // Basketball Total
  223: { tr: 'Handikap', en: 'Handicap', de: 'Handicap' }, // Basketball Handicap
};

export const translateMarketFilterLabel = (id: string | number, locale: string): string => {
  const resolved = resolveMarketName(String(id), locale);
  if (resolved) return resolved;

  const dict = marketIdLocales[id];
  if (dict && dict[locale]) {
    return dict[locale];
  }
  
  if (dict && dict['de']) {
    return dict['de'];
  }
  return String(id);
};


export const translateOutcomeLabel = (label: string, locale: string): string => {
  if (!label) return label;
  const l = locale as Locale;
  const dict = locales[l] || locales['de'];
  
  // Create a mapping of the hardcoded German outcome labels to localized strings
  // Many of these are built from '1.Hz', 'Restzeit', 'Heimsieg' etc.
  
  let result = label;
  
  // Replace components safely
  const map: Record<string, string> = {
    '1.Hz Heimsieg Restzeit': dict['outcome.fhHomeWinRemainingTime'] || '1.Hz Home Win Rem.',
    '1.Hz Unent. Restzeit': dict['outcome.fhDrawRemainingTime'] || '1.Hz Draw Rem.',
    '1.Hz Auswärts Restzeit': dict['outcome.fhAwayWinRemainingTime'] || '1.Hz Away Win Rem.',
    '1.Hz Nächstes Tor Heim': dict['outcome.fhNextGoalHome'] || '1.Hz Next Goal Home',
    '1.Hz Nächstes Tor Auswärts': dict['outcome.fhNextGoalAway'] || '1.Hz Next Goal Away',
    '1.Hz Heimsieg': dict['outcome.fhHomeWin'] || '1.Hz Home Win',
    '1.Hz Unentschieden': dict['outcome.fhDraw'] || '1.Hz Draw',
    '1.Hz Auswärtssieg': dict['outcome.fhAwayWin'] || '1.Hz Away Win',
    'Heimsieg Restzeit': dict['outcome.homeWinRemainingTime'] || 'Home Win Rem.',
    'Unent. Restzeit': dict['outcome.drawRemainingTime'] || 'Draw Rem.',
    'Restzeit Heimsieg': dict['outcome.homeWinRemainingTime'] || 'Home Win Rem.',
    'Restzeit Unentschieden': dict['outcome.drawRemainingTime'] || 'Draw Rem.',
    'Restzeit Auswärtssieg': dict['outcome.awayWinRemainingTime'] || 'Away Win Rem.',
    'Auswärtssieg Restzeit': dict['outcome.awayWinRemainingTime'] || 'Away Win Rem.',
    'Nächstes Tor Heim': dict['outcome.nextGoalHome'] || 'Next Goal Home',
    'Nächstes Tor Auswärts': dict['outcome.nextGoalAway'] || 'Next Goal Away',
    'Nächstes Tor': dict['market.nextGoal'] || 'Next Goal',
    'Heimsieg': dict['outcome.homeWin'] || 'Home Win',
    'Unentschieden': dict['outcome.draw'] || 'Draw',
    'Auswärtssieg': dict['outcome.awayWin'] || 'Away Win',
    'Heim': dict['outcome.home'] || 'Home',
    'Gast': dict['outcome.away'] || 'Away',
  };

  // Try full match first
  if (map[label]) {
    return map[label];
  }

  // If not full match, maybe replace parts (like "Heim" / "Gast" in "Next Goal Heim")
  // Sort keys by length descending to replace longest phrases first
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (result.includes(k)) {
      result = result.replace(new RegExp(k, 'g'), map[k]);
    }
  }

  return result;
};
